import { purchasingPaymentNav } from '@/modules/PurchasingPayments/nav'
// The sidebar tree, composed from each module's own nav entries.
//
// Division of labour (see src/modules/README.md):
//   * a module owns its entries' *content* — title key, path, permission gate;
//   * the shell owns *placement* — order, which group an entry sits in, and the
//     group parents themselves, which no single module can own ("Nhân sự"
//     spans Employee, Dayoffs, Business, WorkDay and Resume).
//
// Icons stay here rather than in the modules: they are only ever set on
// top-level entries, which the shell owns, and they are JSX with per-entry
// classNames that are not uniform (Affiliate uses "w-5 h-5", everything else
// "h-4 w-4"). Keeping them here preserves that exactly and keeps module nav
// files JSX-free.
//
// Sidebar title sử dụng namespace:key pattern của i18next.
// Ví dụ: 'dashboard:sidebar_title' → lấy key "sidebar_title" từ namespace "dashboard"
//
// Ba cách dùng permission trong sidebar (preserved from the old
// components/Sidebar/constants.tsx, which this file replaces):
//
// 1. ignorePermission: true — bỏ qua kiểm tra permission (luôn hiển thị).
// 2. Single menu với permission — menu đơn có kiểm tra quyền, qua
//    rsname + require_scopes (and rsnames for an OR over several resources).
// 3. Parent menu với children — menu cha hiển thị khi ít nhất 1 child có quyền.
//
// require_admin: true hides an entry from everyone but super_admin.
import { SidebarType } from '@/types/shared'
import { shellPaths } from '@/app/paths'
import { accountNav } from '@/modules/Account/nav'
import { affiliateNav } from '@/modules/Affiliate/nav'
import { assetNav } from '@/modules/Asset/nav'
import { businessNav } from '@vnetwork/datacentral-business/nav'
import { contractNav } from '@/modules/Contract/nav'
import { customerNav } from '@/modules/Customer/nav'
import { dayoffsNav } from '@vnetwork/datacentral-dayoffs/nav'
import { driveNav } from '@vnetwork/datacentral-drive/nav'
import { employeeNav } from '@/modules/Employee/nav'
import { invoiceNav } from '@/modules/Invoice/nav'
import { livechatNav } from '@vnetwork/datacentral-livechat/nav'
import { mailerCampaignNav } from '@/modules/MailerCampaign/nav'
import { marketingNav } from '@/modules/Marketing/nav'
import { oncallNav } from '@/modules/Oncall/nav'
import { operationsNav } from '@/modules/Operations/nav'
import { reportNav } from '@/modules/Report/nav'
import { resellerNav } from '@vnetwork/datacentral-reseller/nav'
import { resumeNav } from '@/modules/Resume/nav'
import { serviceNav } from '@/modules/Service/nav'
import { settingsNav } from '@/modules/Settings/nav'
import { surveyNav } from '@vnetwork/datacentral-survey/nav'
import { tendersNav } from '@vnetwork/datacentral-tenders/nav'
import { ticketNav } from '@/modules/Ticket/nav'
import { toolsNav } from '@/modules/Tools/nav'
import { vCardNav } from '@/modules/VCard/nav'
import { warehouseNav } from '@vnetwork/datacentral-warehouse/nav'
import { workDayNav } from '@/modules/WorkDay/nav'
import type { ReactNode } from 'react'
import {
    Activity,
    BellRing,
    Briefcase,
    CalendarClock,
    CalendarHeart,
    CalendarOff,
    ChartLine,
    FileSignature,
    FolderPlus,
    HardDrive,
    IdCard,
    LayoutDashboard,
    Megaphone,
    Puzzle,
    Receipt,
    Server,
    Settings,
    ShoppingCart,
    Star,
    Ticket,
    User,
    UserRound,
    Users,
    Wallet,
    Warehouse,
} from 'lucide-react'

export const sidebarMenus: SidebarType[] = [
    {
        icon: <LayoutDashboard className="h-4 w-4" />,
        title: 'dashboard:sidebar_title',
        path: shellPaths.home,
        ignorePermission: true,
    },
    { icon: <Server className="h-4 w-4" />, ...serviceNav.list },
    { icon: <Receipt className="h-4 w-4" />, ...invoiceNav.list },
    {
        icon: <Megaphone className="h-4 w-4" />,
        title: 'marketing:sidebar_leads',
        path: '',
        children: [
            marketingNav.contacts,
            toolsNav.wafFreeReport,
            reportNav.vpbank,
        ],
    },
    {
        icon: <UserRound className="h-4 w-4" />,
        title: 'customer:sidebar_title',
        path: '',
        children: [customerNav.list, resellerNav.list],
    },
    {
        icon: <Users className="h-4 w-4" />,
        title: 'employee:sidebar_hrm',
        path: '',
        children: [
            employeeNav.manage,
            dayoffsNav.manage,
            businessNav.manage,
            workDayNav.manage,
            resumeNav.talentPool,
        ],
    },
    { icon: <Ticket className="h-4 w-4" />, ...ticketNav.list },
    {
        icon: <CalendarClock className="h-4 w-4" />,
        title: 'oncall:oncall_management',
        path: '',
        ignorePermission: true,
        children: [
            oncallNav.dashboard,
            oncallNav.schedules,
            oncallNav.assignments,
            oncallNav.swaps,
            oncallNav.compensations,
        ],
    },
    {
        icon: <Activity className="h-4 w-4" />,
        title: 'operations:sidebar_title',
        path: '',
        children: [
            operationsNav.dashboard,
            operationsNav.services,
            operationsNav.resources,
            operationsNav.orgs,
            operationsNav.cdn,
        ],
    },
    {
        icon: <Warehouse className="h-4 w-4" />,
        ...warehouseNav.capacities,
        // The Warehouse package owns `warehouse:sidebar_title` ("Dung lượng & Tài
        // nguyên", lower case). Host key until the package ships Title Case.
        title: 'sidebar-capacities',
    },
    {
        icon: <FileSignature className="h-4 w-4" />,
        title: 'contract:sidebar_title',
        path: '',
        children: [
            contractNav.customer,
            contractNav.partner,
            contractNav.sample,
            contractNav.negotiation,
        ],
    },
    {
        icon: <Megaphone className="w-5 h-5" />,
        title: 'affiliate:sidebar_title',
        path: '',
        children: [affiliateNav.customer, affiliateNav.employee],
    },
    {
        icon: <ChartLine className="h-4 w-4" />,
        title: 'report:sidebar_title',
        path: '',
        children: [
            reportNav.customer,
            reportNav.service,
            reportNav.income,
            reportNav.revenues,
            reportNav.businessTargets,
            reportNav.targetsAnalytics,
            reportNav.financials,
            reportNav.serviceAnalytics,
            reportNav.receivables,
        ],
    },
    { icon: <HardDrive className="h-4 w-4" />, ...driveNav.list },
    { icon: <FolderPlus className="h-4 w-4" />, ...assetNav.list },
    {
        icon: <ShoppingCart className="h-4 w-4" />,
        title: 'purchasing-payments:sidebar-title',
        path: '',
        ignorePermission: true,
        children: [
            purchasingPaymentNav.purchase,
            purchasingPaymentNav.advance,
            purchasingPaymentNav.expense,
        ],
    },
    {
        icon: <Puzzle className="h-4 w-4" />,
        title: 'extension',
        path: '',
        children: [
            tendersNav.list,
            surveyNav.list,
            mailerCampaignNav.campaigns,
            livechatNav.list,
            toolsNav.convertWebp,
        ],
    },
    {
        icon: <Settings className="h-4 w-4" />,
        title: 'settings:sidebar_title',
        path: '',
        ignorePermission: true,
        children: [
            settingsNav.serviceTemplates,
            settingsNav.slaTemplates,
            settingsNav.regulations,
            settingsNav.productService,
            settingsNav.dataAccess,
        ],
    },
]

/**
 * Account entries, shown in the topbar account menu rather than the sidebar
 * (AccountDropdown). Same SidebarType gates as sidebar entries.
 */
export const accountMenu: { item: SidebarType; icon: ReactNode }[] = [
    { item: accountNav.profile, icon: <User className="h-4 w-4" /> },
    { item: businessNav.account, icon: <Briefcase className="h-4 w-4" /> },
    { item: dayoffsNav.account, icon: <CalendarOff className="h-4 w-4" /> },
    { item: vCardNav.list, icon: <IdCard className="h-4 w-4" /> },
    { item: accountNav.oncall, icon: <BellRing className="h-4 w-4" /> },
    { item: workDayNav.account, icon: <CalendarClock className="h-4 w-4" /> },
    { item: accountNav.myIncomes, icon: <Wallet className="h-4 w-4" /> },
    { item: accountNav.myRatings, icon: <Star className="h-4 w-4" /> },
    {
        item: accountNav.calendarEvents,
        icon: <CalendarHeart className="h-4 w-4" />,
    },
]
